import React, { FC, useEffect, useState } from 'react';
import {
    Button,
    ButtonGroup,
    ClickAwayListener,
    Grow,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useNavigate } from 'react-router-dom';

interface Props {
    relatedEvents: string[];
    selectedIndex: number;
    setSelectedIndex: (index: number) => void;
    eventName: string;
}

export const RelatedEvents: FC<Props> = ({ relatedEvents, selectedIndex, setSelectedIndex, eventName }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef<HTMLDivElement>(null);

    const handleMenuItemClick = (
        event: React.MouseEvent<HTMLLIElement, MouseEvent>,
        index: number,
        formattedRelatedEvent: string,
    ) => {
        setSelectedIndex(index);
        setOpen(false);
        navigate(`/event/${eventName}/discipline/${formattedRelatedEvent}`);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: Event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        setOpen(false);
    };

    return (
        <>
            {relatedEvents.length > 0 && (
                <>
                    {!isMobile && (
                        <ButtonGroup aria-label="Basic button group" sx={{ marginTop: '1rem' }}>
                            {relatedEvents.map((relatedEvent, index) => {
                                const formattedRelatedEvent = relatedEvent
                                    .replace(/\s+/g, '-')
                                    .replace(/\//g, '-')
                                    .toLowerCase();
                                return (
                                    <Button
                                        key={index}
                                        size="small"
                                        variant={selectedIndex === index ? 'contained' : undefined}
                                        onClick={() => {
                                            setSelectedIndex(index);
                                            navigate(`/event/${eventName}/discipline/${formattedRelatedEvent}`);
                                        }}
                                    >
                                        {relatedEvent}
                                    </Button>
                                );
                            })}
                        </ButtonGroup>
                    )}

                    {isMobile && (
                        <>
                            <ButtonGroup
                                variant="contained"
                                ref={anchorRef}
                                aria-label="Button group with a nested menu"
                                sx={{ width: '100%', marginTop: '1rem' }}
                            >
                                <Button onClick={handleToggle} sx={{ width: '100%' }}>
                                    {relatedEvents[selectedIndex]}
                                </Button>
                                <Button
                                    size="small"
                                    aria-controls={open ? 'split-button-menu' : undefined}
                                    aria-expanded={open ? 'true' : undefined}
                                    aria-label="select merge strategy"
                                    aria-haspopup="menu"
                                    onClick={handleToggle}
                                >
                                    <ArrowDropDownIcon />
                                </Button>
                            </ButtonGroup>
                            <Popper
                                sx={{ zIndex: 1, width: '85%' }}
                                open={open}
                                anchorEl={anchorRef.current}
                                role={undefined}
                                transition
                                disablePortal
                            >
                                {({ TransitionProps, placement }) => (
                                    <Grow
                                        {...TransitionProps}
                                        style={{
                                            transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                                        }}
                                    >
                                        <Paper>
                                            <ClickAwayListener onClickAway={handleClose}>
                                                <MenuList
                                                    id="split-button-menu"
                                                    autoFocusItem
                                                    sx={{
                                                        backgroundColor: (theme) =>
                                                            theme.palette.mode === 'light'
                                                                ? 'rgba(203,203,203)'
                                                                : undefined,
                                                    }}
                                                >
                                                    {relatedEvents.map((relatedEvent, index) => {
                                                        const formattedRelatedEvent = relatedEvent
                                                            .replace(/\s+/g, '-')
                                                            .replace(/\//g, '-')
                                                            .toLowerCase();
                                                        return (
                                                            <MenuItem
                                                                key={relatedEvent}
                                                                selected={index === selectedIndex}
                                                                onClick={(event) =>
                                                                    handleMenuItemClick(
                                                                        event,
                                                                        index,
                                                                        formattedRelatedEvent,
                                                                    )
                                                                }
                                                            >
                                                                {relatedEvent}
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </MenuList>
                                            </ClickAwayListener>
                                        </Paper>
                                    </Grow>
                                )}
                            </Popper>
                        </>
                    )}
                </>
            )}
        </>
    );
};
